import FormatCurrency from 'components/Common/FormatCurrency'
import Image from 'components/Common/Image'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import TextLink from 'LuxKit/TextLink'
import Group from 'components/utils/Group'
import config from 'constants/config'
import { INTL_DAY_MONTH_NAME_SHORT, INTL_DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { fareCabins } from 'constants/flight'
import { isSameYear, useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { rem } from 'polished'
import React, { Fragment, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { middleDotSeparatorMessage } from 'lib/intl/separators'
import styled from 'styled-components'
import FlightDealPoints from '../FlightMerchandising/FlightDealPoints'

const Logo = styled(Image)`
  width: 100%;
  max-width: ${rem(70)};
`

interface Props {
  deal: App.FlightDeal
  alternativeClassFlightDeals?: Array<App.FlightDeal>
}

export function FlightDealInfoCard(props: Props) {
  const { deal, alternativeClassFlightDeals } = props
  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()

  const travelPeriodsLabels = useMemo(() => {
    return deal.travelPeriods.map((period) => {
      const sameYear = isSameYear(new Date(period.departureDate), new Date(period.arrivalDate))
      return `${dateFormatter(period.departureDate, sameYear ? INTL_DAY_MONTH_NAME_SHORT : INTL_DMY_CASUAL_SHORT_FORMAT)} - ${dateFormatter(period.arrivalDate, INTL_DMY_CASUAL_SHORT_FORMAT)}`
    })
  }, [dateFormatter, deal.travelPeriods])

  const dealClass = useMemo(() => {
    return fareCabins.find(type => type.value === deal.fareClass)
  }, [deal])

  return (
    <VerticalSpacer gap={8}>
      <Group gap="0 8" direction="horizontal" verticalAlign="center" horizontalAlign="space-between">
        <StructureLabel variant="standard">
          {dealClass?.label}
        </StructureLabel>

        {deal.carrier.logo && <Logo image={deal.carrier.logo} />}
      </Group>

      <div>
        <BodyTextBlock variant="small">
          {intl.formatMessage({
            id: 'rbkUOI4sW9',
            defaultMessage: 'Travel between',
            description: 'Label before travel period dates on flight deal info card',
          })}
        </BodyTextBlock>

        <Group direction="horizontal" wrap="wrap" gap="0 8">
          {travelPeriodsLabels.map((period, index) => <Fragment key={index}>
            <BodyTextBlock variant="medium" weight="bold">
              {period}
            </BodyTextBlock>

            {index < travelPeriodsLabels.length - 1 && <BodyTextBlock variant="medium" weight="bold">
              {intl.formatMessage(middleDotSeparatorMessage)}
            </BodyTextBlock>}
          </Fragment>)}
        </Group>
      </div>

      {deal.dealDescription && <BodyTextBlock variant="small">
        {deal.dealDescription}
      </BodyTextBlock>}

      <FlightDealPoints flightDeal={deal} />

      <VerticalSpacer gap={4}>
        {alternativeClassFlightDeals?.map((alternativeDeal, index) => (
          <div key={index}>
            <TextLink
              size="small"
              to={`/flight/deal/${alternativeDeal.id}`}
              target={config.OPEN_NEW_TAB_OFFER_CLICK ? '_blank' : undefined}
            >
              <FormattedMessage
                id="K/VXf/YZzk"
                defaultMessage="View {price} {fareClass} class deal"
                description="Link text to view an alternative fare class flight deal"
                values={{
                  price: <FormatCurrency value={alternativeDeal.perAdultPrice} />,
                  fareClass: alternativeDeal.fareClass,
                }}
              />
            </TextLink>
          </div>))}
      </VerticalSpacer>
    </VerticalSpacer>
  )
}
